// Importación masiva (Mercado Libre y Tiendanube): qué mostrar según el estado que
// devuelve el backend. Sin red ni DOM, para testearlo solo.
// ADR-2026-09-29-importacion-masiva-front.

export type EstadoImportacion =
  | "listando"
  | "importando"
  | "analizando"
  | "terminada"
  | "cancelada"
  | "fallida";

export interface VistaImportacion {
  id: string;
  origen: "mercadolibre" | "tiendanube";
  estado: EstadoImportacion;
  disparo: "conexion" | "reconexion" | "manual" | "aviso";
  motivo: string | null;
  total: number;
  procesados: number;
  traidos: number;
  ya_estaban: number;
  ignorados: number;
  fallidos: number;
  productos_nuevos: number;
  analisis_total: number;
  analizados: number;
  analisis_fallidos: number;
  creada_at: string;
  terminada_at: string | null;
}

export interface EstadoDeImportaciones {
  activa: VistaImportacion | null;
  ultima: VistaImportacion | null;
}

const NOMBRE: Record<VistaImportacion["origen"], string> = {
  mercadolibre: "Mercado Libre",
  tiendanube: "Tiendanube",
};

const ESTADOS_EN_CURSO: EstadoImportacion[] = [
  "listando",
  "importando",
  "analizando",
];

export const enCurso = (
  i: VistaImportacion | null | undefined,
): i is VistaImportacion => !!i && ESTADOS_EN_CURSO.includes(i.estado);

export interface LineaDeProgreso {
  texto: string;
  /** 0 a 1; null = todavía no se sabe cuánto falta (barra sin avance). */
  avance: number | null;
}

/**
 * Lo que dice la barra de progreso de Mis productos. Mientras trae, manda el conteo de
 * PUBLICACIONES en Mercado Libre (en su modelo nuevo cada color es una publicación: 6 publicaciones
 * pueden ser 4 productos, por eso no dice "productos") y de PRODUCTOS en Tiendanube (uno es uno);
 * cuando ya trajo todo, el de
 * fotos analizadas. Si las dos cosas pasan a la vez, la segunda va al lado, más corta.
 */
export function lineasDeProgreso(i: VistaImportacion): {
  principal: LineaDeProgreso;
  secundaria: LineaDeProgreso | null;
} {
  const origen = NOMBRE[i.origen];
  const cosas = i.origen === "tiendanube" ? "productos" : "publicaciones";
  const analizadas = i.analizados + i.analisis_fallidos;
  const analisis: LineaDeProgreso | null =
    i.analisis_total > 0
      ? {
          texto: `Analizando fotos · ${analizadas}/${i.analisis_total}`,
          avance: analizadas / i.analisis_total,
        }
      : null;
  if (i.estado === "listando") {
    return {
      principal: {
        texto:
          i.total > 0
            ? `Buscando ${cosas} en ${origen} · ${i.total}`
            : `Buscando ${cosas} en ${origen}…`,
        avance: null,
      },
      secundaria: null,
    };
  }
  if (i.estado === "importando") {
    return {
      principal: {
        texto: `Trayendo ${cosas} de ${origen} · ${i.procesados}/${i.total}`,
        avance: i.total > 0 ? i.procesados / i.total : null,
      },
      secundaria: analisis
        ? { ...analisis, texto: `Fotos · ${analizadas}/${i.analisis_total}` }
        : null,
    };
  }
  // analizando: ya trajo todo.
  return {
    principal: analisis ?? { texto: "Analizando fotos…", avance: null },
    secundaria: null,
  };
}

const plural = (n: number, uno: string, varios: string) =>
  `${n} ${n === 1 ? uno : varios}`;

/**
 * Una línea sobre la última importación, si terminó hace poco (para no dejar un cartel viejo para
 * siempre). null = no hay nada que contar.
 */
export function resumenDeUltima(
  i: VistaImportacion | null,
  ahora = Date.now(),
  ventanaMs = 24 * 3600_000,
): string | null {
  if (!i || ESTADOS_EN_CURSO.includes(i.estado) || !i.terminada_at) return null;
  if (ahora - new Date(i.terminada_at).getTime() > ventanaMs) return null;
  if (i.estado === "fallida")
    return i.motivo ?? "No pudimos terminar de traer tus productos.";
  if (i.estado === "cancelada") {
    return i.productos_nuevos > 0
      ? `Importación cortada · ${plural(i.productos_nuevos, "producto traído", "productos traídos")}`
      : null;
  }
  if (i.disparo === "aviso" && i.productos_nuevos === 0) return null;
  if (i.productos_nuevos === 0) return "No había productos nuevos";
  return `Trajimos ${plural(i.productos_nuevos, "producto nuevo", "productos nuevos")}`;
}
